import React, { useCallback, useMemo, useRef } from "react"
import cx from "classnames"

import { Icon, LiveDot, type IconName } from "components/ui"
import { Warning } from "components/feedback"
import OddsValue from "compositions/OddsValue/OddsValue"

import type { SxTicketItem } from "@/contexts/DetailedSxBetslipContext"
import { useDetailedSxBetslip } from "@/contexts/DetailedSxBetslipContext"

import useOddsChange from "src/hooks/useOddsChange"
import { useIsMounted } from "src/hooks"

import { getSportLabel } from "@/SX/helper"
import { formatTextToSlug } from "@/utils/helper"

import messages from "./messages"

interface SxCardProps {
  item: SxTicketItem
  odds: number
  state: string
  isOddsFetching?: boolean
  isStatesFetching?: boolean
}

const SxCard: React.FC<SxCardProps> = ({
  item,
  odds,
  state,
  isOddsFetching = false,
  isStatesFetching = false,
}) => {
  const { removeFromTicket } = useDetailedSxBetslip()
  const nodeRef = useRef<HTMLDivElement>(null)
  const isMounted = useIsMounted()

  useOddsChange({ odds, nodeRef })

  const game = item.game
  const isDisabled = useMemo(
    () => !isStatesFetching && state !== "active",
    [isStatesFetching, state]
  )

  const sportSlug = useMemo(
    () => formatTextToSlug(getSportLabel(game.sportId) || ""),
    [game.sportId]
  )

  const marketName = item.marketName || "Market"
  const leagueName = game.leagueLabel || "International"
  const title = game.gameLabel || game.title
  const selectionName = item.outcome || "Selection"

  const isLive = false // SX doesn't support live bets in this implementation

  const bottomBoxClassName = cx(
    "px-4 py-2 mt-px",
    "group-[.increased]/card:bg-betslip-item-bg-inc",
    "group-[.decreased]/card:bg-betslip-item-bg-dec",
    {
      "bg-betslip-item-bg": !isDisabled,
      "bg-accent-grey-850": isDisabled,
    }
  )
  const oddsClassName = cx(
    "h-5 flex items-center px-[0.375rem] bg-brand-900 border",
    "border-brand-800 rounded-sm text-accent-grey-90 text-caption-12",
    "group-[.increased]/card:bg-accent-green-1000 group-[.increased]/card:border-accent-green-700 group-[.increased]/card:text-accent-green-500",
    "group-[.decreased]/card:bg-accent-red-1000 group-[.decreased]/card:border-accent-red-700 group-[.decreased]/card:text-accent-red-500"
  )

  const handleRemove = useCallback(
    () => removeFromTicket(item.uniqueId || (item as any).orderHash),
    [item, removeFromTicket]
  )

  // Show skeleton until component is mounted to prevent hydration mismatch
  if (!isMounted) {
    return (
      <div className="group/card rounded-md overflow-hidden animate-pulse">
        {/* Header skeleton */}
        <div className="bg-accent-grey-850 py-2 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-caption-12 max-w-[90%]">
              <div className="size-4 mr-1 flex-none bg-accent-grey-700 rounded" />
              <div className="h-4 bg-accent-grey-700 rounded w-20" />
              <div className="size-1 rounded-full bg-accent-grey-700 mx-1" />
              <div className="h-4 bg-accent-grey-700 rounded w-16" />
            </div>
            <div className="size-3 bg-accent-grey-700 rounded" />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <div className="h-4 bg-accent-grey-700 rounded w-32" />
            <div className="size-4 bg-accent-grey-700 rounded" />
          </div>
        </div>
        {/* Bottom skeleton */}
        <div className="px-4 py-2 mt-px bg-betslip-item-bg">
          <div className="flex items-center justify-between">
            <div className="h-4 bg-accent-grey-700 rounded w-40" />
            <div className="h-5 w-12 bg-accent-grey-700 rounded" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={nodeRef} className="group/card rounded-md overflow-hidden">
      {/* Header */}
      <div className="bg-accent-grey-850 py-2 px-4">
        <div className="flex items-center justify-between">
          <div
            className={cx("flex items-center text-caption-12 max-w-[90%]", {
              "text-accent-grey-500": isDisabled,
              "text-accent-grey-200": !isDisabled,
            })}
          >
            <Icon
              className="size-4 mr-1 flex-none"
              name={`sport/${sportSlug}` as IconName}
            />
            <div className="whitespace-nowrap">{leagueName}</div>
            <div className="size-1 rounded-full bg-accent-grey-700 mx-1" />
            <div className="text-ellipsis whitespace-nowrap overflow-hidden">
              {marketName}
            </div>
          </div>
          <button
            className="text-accent-grey-200 hover:text-accent-grey-30 transition ml-2 p-1"
            onClick={handleRemove}
          >
            <Icon className="size-3" name="interface/close" />
          </button>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center">
            <div
              className={cx("text-caption-13 font-semibold mr-1", {
                "text-accent-grey-500": isDisabled,
              })}
            >
              {title}
            </div>
            {isLive && <LiveDot />}
          </div>
          {isDisabled && (
            <Icon
              className="text-accent-grey-200 size-4"
              name="interface/lock"
            />
          )}
        </div>
      </div>

      {/* Bottom */}
      <div className={bottomBoxClassName}>
        {isDisabled ? (
          <Warning text={messages.locked} />
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="text-caption-12">
                <span className="text-accent-grey-200 mr-1">{marketName}:</span>
                <span>{selectionName}</span>
              </div>
              {isOddsFetching ? (
                <Icon className="size-5" name="interface/spinner" />
              ) : odds === 0 ? (
                <span className={oddsClassName}>--</span>
              ) : (
                <OddsValue className={oddsClassName} odds={odds} />
              )}
            </div>
            {/* {isBatch && (
              <div className="flex items-center mt-2">
                <Input
                  className="bg-accent-grey-800"
                  type="number"
                  // value={batchBetAmount}
                  placeholder="0.00"
                  leftNode={
                    <Icon
                      className="size-4 mr-2"
                      name={constants.currencyIcons[appChain.id]}
                    />
                  }
                  onChange={onBatchAmountChange}
                />
                <div className="text-right ml-3">
                  <Message
                    className="text-caption-12 text-accent-grey-200 w-max"
                    value={messages.possibleWin}
                    tag="p"
                  />
                  <div className="text-caption-13 text-brand-light-500 font-semibold mt-0.5 w-max text-right ml-auto">
                    {formatToFixed(oddsRef.current * +batchBetAmount, 2)}{' '}
                    {betToken.symbol}
                  </div>
                </div>
              </div>
            )} */}
          </>
        )}
      </div>
    </div>
  )
}

export default SxCard
